import { memo, useCallback, useRef, useState } from 'react';
import { Flex, Text, useOutsideClick } from '@chakra-ui/react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { useDispatch, useSelector } from 'react-redux';

import DraftJSFormula from 'pages/BuilderMode/DraftJSFormula';
import DraftJSFormula2 from 'pages/BuilderMode/DraftJSFormula2';
import {
  getIsCalculationSelected,
  getSelectedIndicatorType,
  getSelectedIndicators,
  getToggleInputPanel,
  handleToggleInputPanel,
  UpdateIndicator,
  UpdateIndicatorFormat,
} from 'redux/PlannerModeSlice';
import { capitalizeFirstLetter } from 'utils/GlobalHelpers';

import { BUILDER_MODE_V1_LAYOUT, BUILDER_MODE_V1_TOKENS } from '../../constants';

import DataTypeDropdown from './DataTypeDropdown';
import FormatToggleGroup from './FormatToggleGroup';
import IndicatorTypeDropdown from './IndicatorTypeDropdown';
import ShowInputPanelButton from './ShowInputPanelButton';

import './BuilderModeToolbarV1.scss';

const DATA_FORMAT_OPTIONS = [
  { type: 'Number', label: '.00', value: 'number' },
  { type: 'Integer', label: '123', value: 'integer' },
  { type: 'Percent', label: '%', value: 'percentage' },
  { type: 'Currency', label: '$', value: 'currency' },
] as const;

const BuilderModeToolbarV1 = ({ flags }: { flags?: Record<string, boolean> }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const dispatch: any = useDispatch();
  const toggleInputPanel = useSelector(getToggleInputPanel);
  const selectedIndicators = useSelector(getSelectedIndicators);
  const indicatorType = useSelector(getSelectedIndicatorType);
  const isCalculation = useSelector(getIsCalculationSelected);
  const formulaReference = useRef<HTMLDivElement>(null);

  useOutsideClick({
    ref: formulaReference,
    handler: () => setIsExpanded(false),
  });

  const handleIndicatorTypeChange = useCallback(
    (type: string) => {
      if (!selectedIndicators?.id) return;

      dispatch(
        UpdateIndicator({
          indicatorId: selectedIndicators.id,
          data: { type },
        }),
      );
    },
    [dispatch, selectedIndicators?.id],
  );

  const handleFieldChanges = useCallback(
    (key: string, value: string) => {
      dispatch(
        UpdateIndicator({
          indicatorId: selectedIndicators?.id,
          data: { [key]: value },
        }),
      );
    },
    [dispatch, selectedIndicators?.id],
  );

  const handleFormatChanges = useCallback(
    (value: string) => {
      dispatch(
        UpdateIndicatorFormat({
          indicatorId: selectedIndicators?.id,
          data: { data_format: value },
        }),
      );
    },
    [dispatch, selectedIndicators?.id],
  );

  if (!selectedIndicators) {
    return null;
  }

  const activeFormat = capitalizeFirstLetter(selectedIndicators?.format ?? '');

  return (
    <Flex
      className='builder-mode-v1-toolbar'
      w='100%'
      flexShrink={0}
      mb={4}
      align='center'
      gap={BUILDER_MODE_V1_LAYOUT.formulaToolbarGap}
      p={BUILDER_MODE_V1_LAYOUT.formulaToolbarPadding}
      bg={BUILDER_MODE_V1_TOKENS.borderSubtle}
      border='1px solid'
      borderColor={BUILDER_MODE_V1_TOKENS.border}
      borderRadius={BUILDER_MODE_V1_LAYOUT.formulaToolbarRadius}
      overflowX={flags?.formulaBar ? 'auto' : 'hidden'}
    >
      <Flex flex='1' minW={0} align='center' gap={BUILDER_MODE_V1_LAYOUT.formulaToolbarGap}>
        <IndicatorTypeDropdown indicatorType={indicatorType} onChange={handleIndicatorTypeChange} />
        {isCalculation ? (
          <Flex
            ref={formulaReference}
            className='builder-mode-v1-formula-section'
            flex='1'
            minW={0}
            align='center'
            gap={BUILDER_MODE_V1_LAYOUT.formulaSectionGap}
          >
            <Text
              fontFamily={BUILDER_MODE_V1_LAYOUT.toolbarFontFamily}
              fontSize='20px'
              fontWeight='600'
              color={BUILDER_MODE_V1_TOKENS.textDark}
              lineHeight='normal'
              flexShrink={0}
            >
              f(x)
            </Text>
            <Flex flex='1' minW={0}>
              {flags?.draftJsFormulaBar ? (
                <DraftJSFormula2 setIsExpanded={setIsExpanded} isExpanded={isExpanded} />
              ) : (
                <DraftJSFormula setIsExpanded={setIsExpanded} isExpanded={isExpanded} />
              )}
            </Flex>
          </Flex>
        ) : (
          <ShowInputPanelButton
            isOpen={Boolean(toggleInputPanel)}
            onClick={() => dispatch(handleToggleInputPanel())}
          />
        )}
      </Flex>

      {!isExpanded && (
        <Flex
          align='center'
          gap={BUILDER_MODE_V1_LAYOUT.formulaControlsGap}
          h='40px'
          flexShrink={0}
          w={{ base: 'auto', xl: BUILDER_MODE_V1_LAYOUT.formulaControlsWidth }}
          minW={{ base: 'auto', xl: BUILDER_MODE_V1_LAYOUT.formulaControlsWidth }}
        >
          <DataTypeDropdown
            dataType={selectedIndicators?.data_type}
            onChange={(value) => handleFieldChanges('data_type', value)}
          />
          <FormatToggleGroup
            options={[...DATA_FORMAT_OPTIONS]}
            activeFormat={activeFormat}
            onChange={handleFormatChanges}
          />
        </Flex>
      )}
    </Flex>
  );
};

export default withLDConsumer()(memo(BuilderModeToolbarV1));
